/**
 * Common formatting helpers shared across ISG apps.
 * Merged from Compliance (dates, text) and Vault (numbers, files, sensitivity).
 *
 * Everything here follows the ACTIVE LOCALE. These were hardcoded to 'en-GB',
 * which is the one setting that is wrong for three of the estate's four
 * locales — and dangerously so for en-US, where 03/08 means 8 March rather
 * than 3 August. Signatures are unchanged, so call sites need no edits: an
 * app that never adopts i18n stays on en-GB exactly as before.
 */
import { getLocale, t } from '../i18n/index.js';
import { CURRENCIES, DEFAULT_LOCALE } from '../i18n/locales.js';

/* ── Date / Time ── */

/**
 * Format a date as a medium-length date in the active locale.
 * en-GB "04 Aug 2026" · en-US "Aug 04, 2026" · de "04. Aug. 2026" · cs "04. 8. 2026"
 */
export function formatDate(dateStr, options = {}) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString(getLocale(), {
      day: '2-digit', month: 'short', year: 'numeric',
      ...options,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a date and time in the active locale.
 *
 * ⚠️ 24-hour vs 12-hour is deliberately NOT forced — en-US renders "02:30 PM"
 * and the others "14:30", because that is what each audience reads without
 * having to convert.
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString(getLocale(), {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format money. Falls back to the locale's own currency when none is given.
 *
 * ⚠️ Pass the currency the AMOUNT is in — never let it default for a stored
 * value. A price held in GBP is £ whoever is reading; defaulting by locale
 * would relabel it Kč for a Czech viewer without converting anything, which
 * is the worst possible failure for a pricing estate.
 */
export function formatCurrency(amount, currency = null, options = {}) {
  if (amount == null || amount === '') return '';
  const locale = getLocale();
  const code = currency || CURRENCIES[locale] || CURRENCIES[DEFAULT_LOCALE];
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: code, ...options,
    }).format(Number(amount));
  } catch {
    return String(amount);
  }
}

/**
 * Relative time ("2h ago"). Falls back to an absolute date beyond a week.
 */
export function timeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return t('format.just_now');
  if (seconds < 3600) return t('format.minutes_ago', { count: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('format.hours_ago', { count: Math.floor(seconds / 3600) });
  if (seconds < 604800) return t('format.days_ago', { count: Math.floor(seconds / 86400) });

  return date.toLocaleDateString(getLocale(), {
    day: 'numeric', month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Days from today until target date. Negative = overdue.
 */
export function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const target = new Date(dateStr);
    const today = new Date();
    target.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

/* ── Text ── */

/**
 * Uppercase first letter, replace underscores with spaces.
 */
export function capitalize(str) {
  if (!str) return '';
  return str.replace(/_/g, ' ').charAt(0).toUpperCase() + str.replace(/_/g, ' ').slice(1);
}

/**
 * Truncate to max length with ellipsis.
 */
export function truncate(str, len = 80) {
  if (!str || str.length <= len) return str || '';
  return str.slice(0, len).trimEnd() + '\u2026';
}

/* ── Numbers / Files ── */

/**
 * Format a number with a compact suffix (1.2k · 1,2 Tsd. · 1,2 tis.).
 *
 * Both halves are localised: the suffix comes from the catalogue, and the
 * digits go through Intl so the DECIMAL SEPARATOR is right — "1.2k" reads as
 * twelve-hundred to a British reader and as one-point-two to nobody in
 * Germany, where the same value is written "1,2".
 */
export function formatNumber(num) {
  if (num == null) return '0';
  const locale = getLocale();

  const decimal = (value) => new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  }).format(value);

  if (num >= 1_000_000) return `${decimal(num / 1_000_000)}${t('format.million_suffix')}`;
  if (num >= 1_000) return `${decimal(num / 1_000)}${t('format.thousand_suffix')}`;

  try {
    return new Intl.NumberFormat(locale).format(num);
  } catch {
    return String(num);
  }
}

/**
 * Format bytes to human-readable file size, with the active locale's decimal
 * separator: en-GB "1.5 MB" · de/cs "1,5 MB". English output is unchanged
 * (one decimal above bytes, no digit grouping — "1023 B", not "1,023 B").
 */
export function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB']; // i18n-ignore: SI unit symbols, identical in every locale
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  const digits = i === 0 ? 0 : 1;
  let text;
  try {
    text = new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false,
    }).format(size);
  } catch {
    text = size.toFixed(digits);
  }
  return `${text} ${units[i]}`;
}

/**
 * Estimate reading time from text content.
 */
export function readTime(text) {
  const minutes = text ? Math.max(1, Math.ceil(text.trim().split(/\s+/).length / 200)) : 1;
  return t('format.read_time', { count: minutes });
}

/* ── Domain-specific ── */

/**
 * Sensitivity level → { label, cssClass, icon }.
 */
export function sensitivityInfo(level) {
  const map = {
    public:       { key: 'public',       cssClass: 'badge-green', icon: '\u{1F513}' },
    internal:     { key: 'internal',     cssClass: 'badge-blue',  icon: '\u{1F512}' },
    confidential: { key: 'confidential', cssClass: 'badge-amber', icon: '\u{1F512}' },
    restricted:   { key: 'restricted',   cssClass: 'badge-red',   icon: '\u{1F6AB}' },
  };
  const entry = map[level] || map.internal;
  return { ...entry, label: t(`sensitivity.${entry.key}`) };
}

/**
 * Source type → human-readable label.
 *
 * An UNKNOWN type returns the raw value rather than "Unknown", so a new source
 * added on the backend shows something diagnostic instead of hiding behind a
 * translated placeholder.
 */
export function sourceLabel(sourceType) {
  const known = ['web_upload', 'ftp', 'email', 'api', 'rss', 'scrape', 'erp', 'vault'];
  if (known.includes(sourceType)) return t(`source.${sourceType}`);
  return sourceType || t('source.unknown');
}
